import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { RiskResult, ScenarioData, ScenarioDataSubRow, ScenarioType } from '../types';
import i18n from 'i18next';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import moment from 'moment';
import { getOrderedScenarioData } from '../columns/TableColumns';

export const getScenarioType = (
  data: RiskResult[],
  indicator: number,
  timeStamp: string
): ScenarioType => {
  if (data.length > 6 || Boolean(data.find((risk) => risk.scenarioNumber > 6))) return 'Default';
  if (indicator === 1) return 'LegacyPoolIndicatorOne';

  const scenarioTimeStamp = moment(timeStamp, 'MMMM Do, YYYY');
  const legacyPoolIndicatorBefore2020TimeStamp = moment('Sept 26, 2020', 'MMM D, YYYY');
  const isLegacyPoolIndicatorBefore2020 =
    indicator === 0 &&
    moment(scenarioTimeStamp).isBefore(moment(legacyPoolIndicatorBefore2020TimeStamp));

  if (isLegacyPoolIndicatorBefore2020) return 'LegacyPoolIndicatorBefore2020';

  return 'LegacyPoolIndicatorZero2020To2026';
};

const calculateTotal = (...args: number[]) => {
  return args.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

const formatData = (key: string, value: number | string) => {
  const dollarAmount = [
    'mliPremiumDueAmount',
    'ontarioProvincialSalesTaxDueAmount',
    'quebecProvinceSalesTaxDueAmount',
    'manitobaProvinceSalesTaxDueAmount',
    'saskatchewanProvinceSalesTaxDueAmount',
    'totalLoanAmount',
    'calculateTotal'
  ];

  const rate = [
    'expectedClaimRatePercent',
    'adjustedEstimatedClaimRatePercent',
    'premiumFlatRatePercent',
    'premiumDiscount',
    'basePremiumRate'
  ];
  if (dollarAmount.includes(key)) {
    return formatAmountWithDollar(value, i18n.language, true);
  }

  if (rate.includes(key)) {
    const fractionDigit = key === 'premiumDiscount' ? 1 : 4;
    return formatRatio(Number(value), i18n.language, fractionDigit);
  }
  return value.toString();
};

const getNumericValue = (value: number | string | null | undefined): number => {
  if (value == null) return 0;
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const TransposeData = (
  data: RiskResult[],
  rowHeader: ScenarioData[],
  scenarioType: ScenarioType,
  indicator: number,
  timeStamp: string
): ScenarioData[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const orderedData = getOrderedScenarioData(data, indicator, timeStamp, scenarioType);
  if (orderedData.length === 0) {
    return [];
  }

  const transposedData: ScenarioData[] = [];

  rowHeader.forEach((feature) => {
    const newRow: ScenarioData = { rowHeader: feature.rowHeader };
    let newSubRow: ScenarioDataSubRow[] = [];
    feature.subRows?.forEach((row) => {
      const newSubRowData: ScenarioDataSubRow = { rowHeader: row.rowHeader, key: row.key };
      orderedData.forEach((originalRow, index) => {
        let newKey;
        const counter = index + 1;
        if (scenarioType === 'Default') {
          newKey = `yearTermInsurance${counter}` as keyof ScenarioDataSubRow;
        } else {
          newKey = `scenario${counter}` as keyof ScenarioDataSubRow;
        }

        if (row.key === 'calculateTotal') {
          newSubRowData[newKey] = formatData(
            row.key,
            calculateTotal(
              getNumericValue(originalRow.mliPremiumDueAmount),
              getNumericValue(originalRow.ontarioProvincialSalesTaxDueAmount),
              getNumericValue(originalRow.quebecProvinceSalesTaxDueAmount),
              getNumericValue(originalRow.manitobaProvinceSalesTaxDueAmount),
              getNumericValue(originalRow.saskatchewanProvinceSalesTaxDueAmount)
            )
          );
        } else {
          const value = originalRow[row.key as keyof RiskResult];
          newSubRowData[newKey] = formatData(row.key, (value ?? 0) as number | string);
        }
      });
      newSubRow.push(newSubRowData);
    });
    newRow['subRows'] = newSubRow;
    transposedData.push(newRow);
  });
  return transposedData;
};
